import {
  CSSProperties,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import cx from "classnames";
import { useUnit } from "effector-react";

import { Touch, TouchGestureEvent } from "../Touch";
import {
  useCameraViewOffset,
  getCurtainOffset as calculateCurtainOffset,
  type ChatPanelMode,
  APP_INSET,
  HUD_INDENT,
  CHAT_INPUT_HEIGHT,
  PANEL_SUGGESTIONS_GAP,
  SUGGESTIONS_HEIGHT,
  CANVAS_PANEL_GAP,
  HALF_SCREEN_SCENE_HEIGHT,
} from "../../context/CameraViewOffsetContext";
import { $safeAreaInsets, type SafeAreaInsets } from "../../state/safeArea";
import { readEffectiveViewportMetrics } from "../../utils/visualViewportMetrics";
import styles from "./AssistantChatPanel.module.css";

const modeClasses = {
  hidden: styles.hidden,
  // "third-screen": styles.modeThirdScreen,
  "half-screen": styles.modeHalfScreen,
  "full-screen": styles.modeFullScreen,
} as const;

const SCENE_PEEK_HEIGHT = 92;
const KEYBOARD_SCENE_PEEK = 14; // minimum gap for dragger bar visibility when keyboard is open
const DRAGGER_OVERHANG = 28; // how far .dragger extends above the panel (inset: -28px)
const FLICK_VELOCITY_THRESHOLD = 0.35; // px per ms

const modeHeightExpression = {
  hidden: `var(--safe-bottom) + var(--chatInputHeight) + 2 * var(--hud-indent) + var(--after-footer-height, 0px)`,
  "half-screen": `var(--vvh) * 100 - var(--safe-top) - 2 * var(--app-inset) - var(--canvas-panel-gap) - ${HALF_SCREEN_SCENE_HEIGHT}px`,
  "full-screen": `var(--vvh) * 100 - var(--safe-top) - 2 * var(--app-inset) - var(--scene-peek, ${SCENE_PEEK_HEIGHT}px)`,
};

export type AssistantChatMode = ChatPanelMode;

export type AssistantChatPanelHandle = {
  handleDragMove: (event: TouchGestureEvent) => void;
  handleDragEnd: (event: TouchGestureEvent) => void;
};

type DragLayoutSnapshot = {
  viewportHeight: number;
  safeArea: SafeAreaInsets;
  keyboardBottomOffset: number;
  scenePeek: number;
};

const getPanelBaseHeight = (
  mode: AssistantChatMode,
  viewportHeight: number,
  safeArea: SafeAreaInsets,
  afterFooterHeight = 0,
  scenePeek = SCENE_PEEK_HEIGHT,
): number => {
  switch (mode) {
    case "hidden":
      return safeArea.bottom + CHAT_INPUT_HEIGHT + 2 * HUD_INDENT + afterFooterHeight;
    case "half-screen":
      return viewportHeight - safeArea.top - 2 * APP_INSET - CANVAS_PANEL_GAP - HALF_SCREEN_SCENE_HEIGHT;
    case "full-screen":
      return viewportHeight - safeArea.top - 2 * APP_INSET - scenePeek;
  }
};

const readKeyboardBottomOffset = (): number => {
  return readEffectiveViewportMetrics().keyboardBottomOffset;
};

const isEditableElement = (
  element: Element | null
): element is HTMLElement => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element.isContentEditable
  );
};

type AssistantChatPanelProps = {
  children: ReactNode;
  footer: ReactNode;
  afterFooter?: ReactNode;
  afterFooterHeight?: number;
  pinnedHeader?: ReactNode;
  pinnedBarHeight?: number;
  onModeChange?: (mode: AssistantChatMode) => void;
  onGestureChange?: (isSliding: boolean) => void;
  onKeyboardDismissGestureStart?: () => void;
  requestedMode?: AssistantChatMode | null;
  dragRef?: MutableRefObject<AssistantChatPanelHandle | null>;
  style?: CSSProperties;
  animateInitialOffset?: boolean;
  /** When true, the panel is hidden via display:none (preserves DOM & scroll position). */
  hidden?: boolean;
};

export function AssistantChatPanel({
  children,
  footer,
  afterFooter,
  afterFooterHeight = 0,
  pinnedHeader,
  pinnedBarHeight = 0,
  onModeChange,
  onGestureChange,
  onKeyboardDismissGestureStart,
  requestedMode,
  dragRef,
  style: externalStyle,
  animateInitialOffset,
  hidden,
}: AssistantChatPanelProps) {
  const [mode, setMode] = useState<AssistantChatMode>("hidden");
  const [shift, setShift] = useState(0);
  const [isSliding, setIsSliding] = useState(false);
  const [duration, setDuration] = useState(animateInitialOffset ? 1200 : 0);

  // Auto-reset duration after transition completes so organic viewport changes
  // (keyboard close, resize) apply instantly — keeps CSS canvas and camera offset in sync.
  useEffect(() => {
    if (duration === 0) return;
    const timer = setTimeout(() => setDuration(0), duration);
    return () => clearTimeout(timer);
  }, [duration]);

  const [layoutSnapshot, setLayoutSnapshot] =
    useState<DragLayoutSnapshot | null>(null);
  const modeRef = useRef(mode);
  const shiftRef = useRef(shift);
  const isSlidingRef = useRef(isSliding);
  const layoutSnapshotRef = useRef<DragLayoutSnapshot | null>(null);
  const dismissedKeyboardRef = useRef(false);
  const animateInitialRef = useRef(animateInitialOffset ?? false);

  const [isMountReady, setIsMountReady] = useState(!animateInitialOffset);
  useEffect(() => {
    if (!animateInitialOffset) return;
    let id = requestAnimationFrame(() => {
      id = requestAnimationFrame(() => setIsMountReady(true));
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const { applyCurtainOffset, viewport } = useCameraViewOffset();
  const safeArea = useUnit($safeAreaInsets);

  const isKeyboardOpen = readEffectiveViewportMetrics().keyboardOpen;
  const scenePeek = isKeyboardOpen ? KEYBOARD_SCENE_PEEK : SCENE_PEEK_HEIGHT;

  const getLiveLayoutSnapshot = useCallback(
    (): DragLayoutSnapshot => ({
      viewportHeight: viewport.height,
      safeArea: { ...safeArea },
      keyboardBottomOffset: readKeyboardBottomOffset(),
      scenePeek,
    }),
    [safeArea, viewport.height, scenePeek]
  );

  const clearLayoutSnapshot = useCallback(() => {
    layoutSnapshotRef.current = null;
    setLayoutSnapshot(null);
  }, []);

  const captureLayoutSnapshot = useCallback(() => {
    const nextSnapshot = getLiveLayoutSnapshot();
    layoutSnapshotRef.current = nextSnapshot;
    setLayoutSnapshot(nextSnapshot);
    return nextSnapshot;
  }, [getLiveLayoutSnapshot]);

  const getCurtainOffsetForLayout = useCallback(
    (nextMode: AssistantChatMode, nextShift: number, snapshot: DragLayoutSnapshot) =>
      calculateCurtainOffset(
        nextMode,
        snapshot.viewportHeight,
        nextShift,
        snapshot.safeArea,
        snapshot.keyboardBottomOffset,
        afterFooterHeight,
      ),
    [afterFooterHeight]
  );

  useEffect(() => {
    onModeChange?.(mode);
  }, [mode, onModeChange]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    shiftRef.current = shift;
  }, [shift]);

  useEffect(() => {
    isSlidingRef.current = isSliding;
  }, [isSliding]);

  useEffect(() => {
    layoutSnapshotRef.current = layoutSnapshot;
  }, [layoutSnapshot]);

  useEffect(() => {
    const snapshot =
      isSlidingRef.current && layoutSnapshotRef.current != null
        ? layoutSnapshotRef.current
        : getLiveLayoutSnapshot();
    const shouldAnimate = animateInitialRef.current;
    if (shouldAnimate) animateInitialRef.current = false;
    applyCurtainOffset(
      getCurtainOffsetForLayout(modeRef.current, shiftRef.current, snapshot),
      shouldAnimate,
      shouldAnimate ? duration : undefined,
    );
  }, [
    applyCurtainOffset,
    getCurtainOffsetForLayout,
    getLiveLayoutSnapshot,
    isSliding,
    layoutSnapshot,
    duration,
  ]);

  useEffect(() => {
    onGestureChange?.(isSliding);
  }, [isSliding, onGestureChange]);

  useEffect(() => {
    if (!requestedMode || requestedMode === modeRef.current) {
      return;
    }

    const nextDuration = modeRef.current === "hidden" ? 600 : 300;
    const snapshot = getLiveLayoutSnapshot();
    setMode(requestedMode);
    setDuration(nextDuration);
    setShift(0);
    setIsSliding(false);
    isSlidingRef.current = false;
    clearLayoutSnapshot();
    applyCurtainOffset(
      getCurtainOffsetForLayout(requestedMode, 0, snapshot),
      true,
      nextDuration
    );
  }, [
    applyCurtainOffset,
    clearLayoutSnapshot,
    getCurtainOffsetForLayout,
    getLiveLayoutSnapshot,
    requestedMode,
  ]);

  const onMove = useCallback(
    (event: TouchGestureEvent) => {
      if (event.isSlideY && typeof event.shiftY === "number") {
        const nextShift = event.shiftY;
        const snapshot = isSlidingRef.current
          ? (layoutSnapshotRef.current ?? captureLayoutSnapshot())
          : captureLayoutSnapshot();

        if (nextShift > 0 && !dismissedKeyboardRef.current) {
          const activeElement = document.activeElement;
          if (isEditableElement(activeElement)) {
            dismissedKeyboardRef.current = true;
            onKeyboardDismissGestureStart?.();
            activeElement.blur();
          }
        }

        setShift(nextShift);
        setIsSliding(true);
        isSlidingRef.current = true;
        applyCurtainOffset(
          getCurtainOffsetForLayout(modeRef.current, nextShift, snapshot),
          false
        );
      }
    },
    [
      applyCurtainOffset,
      captureLayoutSnapshot,
      getCurtainOffsetForLayout,
      onKeyboardDismissGestureStart,
    ]
  );

  const onEnd = useCallback(
    (event: TouchGestureEvent) => {
      const snapshot = layoutSnapshotRef.current ?? getLiveLayoutSnapshot();
      const gestureDuration = Math.max(
        1,
        Math.floor(Number(new Date()) - Number(event.startT))
      );
      const shiftY = event.shiftY ?? 0;
      const velocityY = shiftY / gestureDuration;
      const isFling = Math.abs(velocityY) > FLICK_VELOCITY_THRESHOLD;

      const panelBaseHeight = getPanelBaseHeight(
        modeRef.current,
        snapshot.viewportHeight,
        snapshot.safeArea,
        afterFooterHeight,
        snapshot.scenePeek,
      );
      const panelTopY = snapshot.viewportHeight - panelBaseHeight + shiftY;
      const y = isFling ? panelTopY + 0.5 * duration * velocityY : panelTopY;

      // Dynamic snap boundaries: midpoints between actual mode positions.
      // Adapts automatically when layout constants change (scene height, safe area, etc.).
      const modeArgs = [snapshot.viewportHeight, snapshot.safeArea, afterFooterHeight, snapshot.scenePeek] as const;
      const fullScreenTop = snapshot.viewportHeight - getPanelBaseHeight("full-screen", ...modeArgs);
      const halfScreenTop = snapshot.viewportHeight - getPanelBaseHeight("half-screen", ...modeArgs);
      const hiddenTop = snapshot.viewportHeight - getPanelBaseHeight("hidden", ...modeArgs);
      const fullHalfBoundary = (fullScreenTop + halfScreenTop) / 2;
      const halfHiddenBoundary = (halfScreenTop + hiddenTop) / 2;

      let nextMode: AssistantChatMode;
      switch (true) {
        case y < fullHalfBoundary:
          nextMode = "full-screen";
          break;

        case y < halfHiddenBoundary:
          nextMode = "half-screen";
          break;

        default:
          nextMode = "hidden";
          break;
      }

      setMode(nextMode);
      const nextDuration = Math.max(100, Math.min(600, gestureDuration));
      setDuration(nextDuration);
      setShift(0);
      setIsSliding(false);
      isSlidingRef.current = false;
      clearLayoutSnapshot();
      applyCurtainOffset(
        getCurtainOffsetForLayout(nextMode, 0, getLiveLayoutSnapshot()),
        true,
        nextDuration
      );
      dismissedKeyboardRef.current = false;
    },
    [
      afterFooterHeight,
      applyCurtainOffset,
      clearLayoutSnapshot,
      duration,
      getCurtainOffsetForLayout,
      getLiveLayoutSnapshot,
    ]
  );

  const onOverlayEnd = useCallback(
    (event: TouchGestureEvent) => {
      if (!event.isSlide && modeRef.current === "full-screen") {
        setMode("half-screen");
        setDuration(300);
        applyCurtainOffset(
          getCurtainOffsetForLayout("half-screen", 0, getLiveLayoutSnapshot()),
          true,
          300,
        );
        return;
      }
      onEnd(event);
    },
    [onEnd, applyCurtainOffset, getCurtainOffsetForLayout, getLiveLayoutSnapshot],
  );

  const onFooterEnd = useCallback(
    (event: TouchGestureEvent) => {
      if (event.isSlideY) {
        onEnd(event);
      }
    },
    [onEnd]
  );

  useEffect(() => {
    if (dragRef) {
      dragRef.current = { handleDragMove: onMove, handleDragEnd: onEnd };
    }
  }, [dragRef, onMove, onEnd]);

  // useLayoutEffect ensures CSS vars update synchronously after DOM mutations
  // (before the browser paints). This keeps --panel-current-height and
  // --panel-transition-duration in sync with the panel's inline height/transition,
  // preventing a 1-frame lag for elements that depend on these vars (canvas, buttons).
  useLayoutEffect(() => {
    const root: HTMLElement | null = document.querySelector('#app')
    if (!root) return;

    let base = 0;
    switch (mode) {
      case "hidden":
        base = safeArea.bottom + CHAT_INPUT_HEIGHT + 2 * HUD_INDENT + afterFooterHeight;
        break;
      case "half-screen":
        base = viewport.height - safeArea.top - 2 * APP_INSET - CANVAS_PANEL_GAP - HALF_SCREEN_SCENE_HEIGHT;
        break;
      case "full-screen":
        base = viewport.height - safeArea.top - 2 * APP_INSET - scenePeek;
        break;
    }
    const cur = base - shift;

    // Internal suggestions
    const imin = CHAT_INPUT_HEIGHT + 2 * HUD_INDENT + afterFooterHeight;
    const imax = imin + SUGGESTIONS_HEIGHT;
    const ivalue = (cur - imin) / (imax - imin);

    // External suggestions
    const emin = CHAT_INPUT_HEIGHT + 2 * HUD_INDENT + afterFooterHeight;
    const emax = emin + PANEL_SUGGESTIONS_GAP;
    const evalue = 1 - (cur - emin) / (emax - emin);

    const isDragging = shift !== 0;
    root.style.cssText = `
      --internalSuggestionsOpacity: ${ivalue.toFixed(2)};
      --externalSuggestionsOpacity: ${evalue.toFixed(2)};
      --panel-current-height: ${cur.toFixed(0)}px;
      --panel-transition-duration: ${isDragging ? '0s' : `${duration}ms`};
      --scene-blur-opacity: ${mode === "full-screen" && shift === 0 ? 1 : 0};
    `;

    return () => {
      root.style.cssText = '';
    };
  }, [mode, viewport.height, safeArea.top, safeArea.bottom, shift, duration, afterFooterHeight, scenePeek]);

  const dragLayoutSnapshot = isSliding ? layoutSnapshot : null;

  const panelHeight =
    dragLayoutSnapshot == null
      ? `calc(${modeHeightExpression[mode]} - ${shift}px)`
      : `${Math.max(
          0,
          getPanelBaseHeight(
            mode,
            dragLayoutSnapshot.viewportHeight,
            dragLayoutSnapshot.safeArea,
            afterFooterHeight,
            dragLayoutSnapshot.scenePeek,
          ) - shift
        )}px`;

  const panelBottomExpr =
    dragLayoutSnapshot == null
      ? `calc(var(--app-inset) + var(--keyboard-bottom-offset, 0px))`
      : `${APP_INSET + dragLayoutSnapshot.keyboardBottomOffset}px`;

  const isFullScreenNoShift = mode === "full-screen" && shift === 0;

  // Overlay position: fixed to full-screen panel top, extends 28px above to cover dragger
  const fullScreenHeight = `calc(${modeHeightExpression["full-screen"]})`;
  const overlayBottom = `calc(${panelBottomExpr} + ${fullScreenHeight} - ${DRAGGER_OVERHANG}px)`;

  return (
    <>
      {!hidden && (
        <Touch onMoveY={onMove} onEnd={onOverlayEnd}>
          <div
            className={cx(
              styles.scenePeekOverlay,
              isFullScreenNoShift && styles.scenePeekOverlayVisible,
            )}
            style={{
              height: `${scenePeek + DRAGGER_OVERHANG}px`,
              bottom: overlayBottom,
            }}
          />
        </Touch>
      )}
      <div
        className={cx(
          styles.assistantChatPanel,
          modeClasses[mode],
          shift !== 0 && styles.dragging,
          !isMountReady && styles.entering
        )}
        style={
          {
            display: hidden ? 'none' : undefined,
            height: panelHeight,
            bottom:
              dragLayoutSnapshot == null
                ? undefined
                : `${APP_INSET + dragLayoutSnapshot.keyboardBottomOffset}px`,
            "--transitionDuration": `${duration}ms`,
            "--after-footer-height": `${afterFooterHeight}px`,
            "--scene-peek": `${scenePeek}px`,
            ...externalStyle,
          } as CSSProperties
        }
      >
      <Touch onMoveY={onMove} onEnd={onEnd}>
        <div className={styles.dragger} />
      </Touch>
      {pinnedHeader && (
        <Touch onMoveY={onMove} onEnd={onEnd}>
          {pinnedHeader}
        </Touch>
      )}
      <div
        className={cx(styles.wrapper, pinnedBarHeight > 0 && styles.wrapperPinned)}
        style={{ "--pinned-bar-height": `${pinnedBarHeight}px` } as CSSProperties}
      >
        {children}
      </div>

      <Touch onMoveY={onMove} onEnd={onFooterEnd}>
        {footer}
      </Touch>
      {afterFooter && (
        <div className={styles.afterFooter}>{afterFooter}</div>
      )}
    </div>
    </>
  );
}
